/**
 * TanStack Query hooks for the team CRUD + invitations surface.
 *
 * Query-key contract:
 *   ['teams']                              — current user's team list
 *   ['teams', teamId]                      — team detail (members + pending count)
 *   ['teams', teamId, 'invitations']       — invitation list for a team
 *   ['teams', 'pending-invitations']       — incoming pending invites for this phone
 *
 * Mutations invalidate the keys most likely to be displayed after the action,
 * so a successful invite refreshes the invitations table without an explicit
 * refetch in the dialog.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptInvitation,
  archiveTeam,
  cancelInvitation,
  createInvitation,
  createTeam,
  declineInvitationByToken,
  declinePendingInvitation,
  getTeam,
  leaveTeam,
  listInvitations,
  listPendingInvitations,
  listTeams,
  lookupInvitationToken,
  reactivateTeam,
  updateTeam,
  updateMemberRole,
  type CreateInvitationInput,
  type CreateTeamInput,
  type UpdateTeamInput,
} from "@/lib/api/teams";
import { useAuth } from "@/context/AuthContext";

export const teamsKeys = {
  all: ["teams"] as const,
  list: () => [...teamsKeys.all] as const,
  detail: (teamId: string) => [...teamsKeys.all, teamId] as const,
  invitations: (teamId: string) =>
    [...teamsKeys.all, teamId, "invitations"] as const,
  pendingInvitations: () =>
    [...teamsKeys.all, "pending-invitations"] as const,
};

/**
 * List the calling user's active team memberships.
 *
 * Gated on `auth.status === "authenticated"` because this is mounted at the
 * root layout (TeamProvider) and would otherwise fire on every page —
 * including `/login` and `/auth/callback` where there is no token yet,
 * triggering a 401 → onUnauthorized → signIn() cascade that overwrites
 * the in-flight PKCE challenge.
 */
export function useTeams() {
  const auth = useAuth();
  return useQuery({
    queryKey: teamsKeys.list(),
    queryFn: listTeams,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    enabled: auth.status === "authenticated",
  });
}

/** List incoming pending invitations for the signed-in phone (PF-372). */
export function usePendingInvitations() {
  const auth = useAuth();
  return useQuery({
    queryKey: teamsKeys.pendingInvitations(),
    queryFn: listPendingInvitations,
    enabled: auth.status === "authenticated",
  });
}

/** Fetch a single team's detail (members + pending invitation count). */
export function useTeam(teamId: string | undefined) {
  return useQuery({
    queryKey: teamId ? teamsKeys.detail(teamId) : ["teams", "__missing__"],
    queryFn: () => getTeam(teamId!),
    enabled: !!teamId,
  });
}

/** List invitations for a team (owner/admin only). */
export function useInvitations(teamId: string | undefined) {
  return useQuery({
    queryKey: teamId
      ? teamsKeys.invitations(teamId)
      : ["teams", "__missing__", "invitations"],
    queryFn: () => listInvitations(teamId!),
    enabled: !!teamId,
  });
}

/** Create a new team. The caller becomes its owner. */
export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTeamInput) => createTeam(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamsKeys.all });
    },
  });
}

/** Rename / re-slug a team (team admin/owner only). */
export function useUpdateTeam(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTeamInput) => {
      if (!teamId) throw new Error("teamId is required");
      return updateTeam(teamId, input);
    },
    onSuccess: () => {
      if (!teamId) return;
      void qc.invalidateQueries({ queryKey: teamsKeys.all });
    },
  });
}

/**
 * Archive a team (admin/owner). The BFF rejects it with 409
 * TEAM_HAS_ACTIVE_COMPANIES while any company in the team is still active.
 */
export function useArchiveTeam(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!teamId) throw new Error("teamId is required");
      return archiveTeam(teamId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamsKeys.all });
    },
  });
}

/** Lift a team archive (admin/owner). Operator suspensions cannot be lifted here. */
export function useReactivateTeam(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!teamId) throw new Error("teamId is required");
      return reactivateTeam(teamId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamsKeys.all });
    },
  });
}

/** Send a WhatsApp invitation to a phone number for a team. */
export function useCreateInvitation(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvitationInput) => {
      if (!teamId) throw new Error("teamId is required");
      return createInvitation(teamId, input);
    },
    onSuccess: () => {
      if (!teamId) return;
      void qc.invalidateQueries({
        queryKey: teamsKeys.invitations(teamId),
      });
      void qc.invalidateQueries({ queryKey: teamsKeys.detail(teamId) });
    },
  });
}

/** Cancel a pending invitation. */
export function useCancelInvitation(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => {
      if (!teamId) throw new Error("teamId is required");
      return cancelInvitation(teamId, invitationId);
    },
    onSuccess: () => {
      if (!teamId) return;
      void qc.invalidateQueries({
        queryKey: teamsKeys.invitations(teamId),
      });
      void qc.invalidateQueries({ queryKey: teamsKeys.detail(teamId) });
    },
  });
}

/**
 * Redeem a 6-digit invitation code received via WhatsApp.
 *
 * Phone validation lives entirely server-side: the recipient must already be
 * signed in as the invited number — the BFF reads `phone` from the JWT
 * subject, NOT from the request body.
 */
export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string } | { token: string } | { invitationId: string }) =>
      acceptInvitation(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamsKeys.all });
    },
  });
}

/**
 * Public token lookup for the WhatsApp URL-button landing pages (PF-345).
 * Deliberately NOT gated on auth — the invitee usually has no session yet.
 */
export function useInvitationTokenLookup(token: string | undefined) {
  return useQuery({
    queryKey: token
      ? ([...teamsKeys.all, "invite-token", token] as const)
      : (["teams", "invite-token", "__missing__"] as const),
    queryFn: () => lookupInvitationToken(token!),
    enabled: !!token,
    retry: false,
  });
}

/** Invitee-side decline via the cancel token — public, no session needed. */
export function useDeclineInvitationByToken() {
  return useMutation({
    mutationFn: (token: string) => declineInvitationByToken(token),
  });
}

/** Invitee-side decline from the in-app pending list (session phone). */
export function useDeclinePendingInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => declinePendingInvitation(invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamsKeys.pendingInvitations() });
    },
  });
}

/** Leave (or remove) a member from a team. */
export function useLeaveTeam(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => {
      if (!teamId) throw new Error("teamId is required");
      return leaveTeam(teamId, userId);
    },
    onSuccess: () => {
      if (!teamId) return;
      void qc.invalidateQueries({ queryKey: teamsKeys.all });
      void qc.invalidateQueries({ queryKey: teamsKeys.detail(teamId) });
    },
  });
}

export function useUpdateMemberRole(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role?: CreateInvitationInput["role"]; customRoleId?: string }) => updateMemberRole(teamId, input.userId, { role: input.role, customRoleId: input.customRoleId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: teamsKeys.all }); },
  });
}
